import React from 'react';
import { Link } from 'react-router-dom';
import { CheckIcon } from '@heroicons/react/24/outline';
import { PublicLayout } from '../../components/PublicLayout';
import { PublicPageSeo } from '../../components/PublicPageSeo';
import { RelatedLinks } from '../../components/RelatedLinks';
import { getContent } from '../../content/i18n';
import { useLocale, useLocalizedPath } from '../../hooks/useLocale';

export const PricingPage: React.FC = () => {
  const locale = useLocale();
  const localizedPath = useLocalizedPath();
  const content = getContent(locale);
  const { pricing } = content;
  const homePath = locale === 'en' ? '/en' : '/';
  const pagePath = locale === 'en' ? '/en/pricing' : '/pricing';

  return (
    <>
      <PublicPageSeo
        breadcrumbs={[
          { name: content.common.home, path: homePath },
          { name: pricing.title, path: pagePath },
        ]}
        productPlans={pricing.plans.map((p) => ({
          name: p.name,
          price: p.price,
          description: p.features.join(', '),
        }))}
      />
      <PublicLayout
        breadcrumbs={[
          { label: content.common.home, href: localizedPath('/') },
          { label: pricing.title },
        ]}
      >
        <section className="py-12">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">{pricing.title}</h1>
          <p className="text-lg text-gray-600 dark:text-gray-300 mb-8 max-w-3xl">{pricing.subtitle}</p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {pricing.plans.map((plan) => (
              <div
                key={plan.name}
                className={`rounded-2xl p-8 border ${
                  plan.highlighted
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 shadow-lg scale-105'
                    : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900'
                }`}
              >
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">{plan.name}</h2>
                <div className="mb-6">
                  <span className="text-4xl font-bold text-gray-900 dark:text-white">{plan.price}</span>
                  <span className="text-gray-500 dark:text-gray-400">{plan.period}</span>
                </div>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-gray-600 dark:text-gray-300">
                      <CheckIcon className="h-5 w-5 text-primary-600 flex-shrink-0 mt-0.5" />
                      {f}
                    </li>
                  ))}
                </ul>
                <Link
                  to={localizedPath('/register')}
                  className="block w-full text-center bg-primary-600 hover:bg-primary-700 text-white py-3 rounded-lg font-medium transition-colors"
                >
                  {content.common.register}
                </Link>
              </div>
            ))}
          </div>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">{pricing.comparisonTitle}</h2>
          <div className="overflow-x-auto mb-8">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="py-3 pr-4 text-gray-900 dark:text-white">{locale === 'zh' ? '功能' : 'Feature'}</th>
                  <th className="py-3 px-4 text-gray-900 dark:text-white">{pricing.plans[0].name}</th>
                  <th className="py-3 px-4 text-gray-900 dark:text-white">{pricing.plans[1].name}</th>
                  <th className="py-3 px-4 text-gray-900 dark:text-white">{pricing.plans[2].name}</th>
                </tr>
              </thead>
              <tbody>
                {pricing.comparisonRows.map((row) => (
                  <tr key={row.feature} className="border-b border-gray-100 dark:border-gray-800">
                    <td className="py-3 pr-4 text-gray-600 dark:text-gray-300">{row.feature}</td>
                    <td className="py-3 px-4 text-gray-600 dark:text-gray-300">{row.basic}</td>
                    <td className="py-3 px-4 text-gray-600 dark:text-gray-300">{row.standard}</td>
                    <td className="py-3 px-4 text-gray-600 dark:text-gray-300">{row.premium}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="text-sm text-gray-500 dark:text-gray-400 mb-8">{pricing.note}</p>
          <p className="text-gray-600 dark:text-gray-300">{content.common.signInForLivePricing}</p>
        </section>

        <RelatedLinks
          links={[
            { label: locale === 'zh' ? '功能特性' : 'Features', href: '/features' },
            { label: locale === 'zh' ? '客户端下载' : 'Download', href: '/download' },
            { label: locale === 'zh' ? '帮助中心' : 'Help', href: '/help' },
          ]}
        />
      </PublicLayout>
    </>
  );
};
